import { uniqBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ReactNode } from 'react';

export type EntityTableItem = {
  expandableContent?: () => ReactNode;
  id: string;
};

export type EntityTablePage<T> = {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type EntityTableFetchData<T> = (
  params: { page: number; pageSize: number },
  extraParams: { fetchAll?: boolean }
) => Promise<EntityTablePage<T>>;

export type SortDirection = 'asc' | 'desc';

type Options<T> = {
  defaultSelectedItems?: T[];
  fetchData: EntityTableFetchData<T>;
  initialPageSize: number;
  onSelectionChange?: (items: T[]) => void;
  onSortChange?: (columnId: string, direction: SortDirection) => void;
};

const SELECT_ALL_PAGE_SIZE = 1000;

/**
 * Data/selection/expansion/sort state of an entity table. Mirrors the TaptapList context from
 * sonata-react-components (which is not exported), so the tables behave like the previous lists:
 * reload on `fetchData` identity change, infinite scroll, select-all across every page.
 */
export function useEntityTableData<T extends EntityTableItem>({
  defaultSelectedItems,
  fetchData,
  initialPageSize,
  onSelectionChange,
  onSortChange
}: Options<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [selectedItems, setSelectedItems] = useState<T[]>(defaultSelectedItems ?? []);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isFetching, setIsFetching] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [pagination, setPagination] = useState({ hasMore: true, page: 1 });
  const [sortState, setSortState] = useState<{ columnId: string; direction: SortDirection } | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setSelectedItems(defaultSelectedItems ?? []);
  }, [defaultSelectedItems]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setItems([]);
    setIsFetching(true);
    setIsInitialLoading(true);
    fetchData({ page: 1, pageSize: initialPageSize }, {})
      .then(response => {
        if (requestIdRef.current === requestId) {
          setItems(response.data);
          setPagination({ hasMore: response.page < response.totalPages, page: 1 });
        }
      })
      .catch(error => {
        // biome-ignore lint/suspicious/noConsole: same logging as TaptapList
        console.error('Error loading data:', error);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsFetching(false);
          setIsInitialLoading(false);
        }
      });
  }, [fetchData, initialPageSize]);

  const loadMore = useCallback(async () => {
    if (!pagination.hasMore || isFetching) {
      return;
    }
    const requestId = ++requestIdRef.current;
    const nextPage = pagination.page + 1;
    setIsFetching(true);
    try {
      const response = await fetchData({ page: nextPage, pageSize: initialPageSize }, {});
      if (requestIdRef.current === requestId) {
        setItems(previous => uniqBy([...previous, ...response.data], 'id'));
        setPagination({ hasMore: nextPage < response.totalPages, page: nextPage });
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: same logging as TaptapList
      console.error('Error loading more data:', error);
    } finally {
      if (requestIdRef.current === requestId) {
        setIsFetching(false);
      }
    }
  }, [fetchData, initialPageSize, isFetching, pagination]);

  const updateSelection = (next: T[]) => {
    setSelectedItems(next);
    onSelectionChange?.(next);
  };

  const selectAllPages = async () => {
    setIsFetching(true);
    try {
      const all: T[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await fetchData({ page, pageSize: SELECT_ALL_PAGE_SIZE }, { fetchAll: true });
        all.push(...response.data);
        totalPages = response.totalPages;
        page += 1;
      } while (page <= totalPages);
      updateSelection(uniqBy(all, 'id'));
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: same logging as TaptapList
      console.error('Failed to select all items', error);
    } finally {
      setIsFetching(false);
    }
  };

  const toggleAllSelections = () => (selectedItems.length ? updateSelection([]) : selectAllPages());

  const toggleSelection = (item: T, selected: boolean) =>
    updateSelection(
      selected ? uniqBy([...selectedItems, item], 'id') : selectedItems.filter(({ id }) => id !== item.id)
    );

  const toggleExpand = (itemId: string) =>
    setExpandedIds(previous => {
      const next = new Set(previous);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });

  const handleSort = (columnId: string, direction: SortDirection) => {
    setSortState({ columnId, direction });
    onSortChange?.(columnId, direction);
  };

  return {
    expandedIds,
    handleSort,
    isFetching,
    isInitialLoading,
    items,
    loadMore,
    pagination,
    selectedItems,
    sortState,
    toggleAllSelections,
    toggleExpand,
    toggleSelection
  };
}
